import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import type { ReactNode } from 'react';
import { Button } from './button';
import { cn } from './utils';

export interface AlertDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly onConfirm: () => void | Promise<void>;
  readonly tone?: 'danger' | 'default';
  readonly loading?: boolean;
  /** Conteúdo extra entre a descrição e os botões (lista de impactos, aviso). */
  readonly children?: ReactNode;
}

/**
 * Diálogo de confirmação destrutiva.
 *
 * Diferente de `<Dialog>`, este NÃO fecha por clique fora nem tem botão "X":
 * o padrão APG do alertdialog exige que o usuário escolha explicitamente entre
 * confirmar e cancelar. O foco inicial vai para o botão de cancelar — em ação
 * destrutiva, o Enter reflexo não pode confirmar.
 */
export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancelar',
  onConfirm,
  tone = 'danger',
  loading = false,
  children,
}: AlertDialogProps) {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-overlay bg-[rgb(15_23_42/0.55)] animate-overlay-in" />
        <AlertDialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-modal w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-lg border border-border bg-surface-raised p-5 shadow-overlay animate-content-in',
          )}
        >
          <AlertDialogPrimitive.Title className="text-lg font-semibold text-content">
            {title}
          </AlertDialogPrimitive.Title>
          <AlertDialogPrimitive.Description className="mt-2 text-sm text-content-muted">
            {description}
          </AlertDialogPrimitive.Description>

          {children ? <div className="mt-3">{children}</div> : null}

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <AlertDialogPrimitive.Cancel asChild>
              {/* Foco inicial: o Radix foca o Cancel por ser o primeiro
                  elemento tabulável do conteúdo. Ordem deliberada. */}
              <Button variant="secondary" disabled={loading}>
                {cancelLabel}
              </Button>
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action asChild>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                loading={loading}
                onClick={(event) => {
                  // Evita o fechamento automático quando a confirmação é
                  // assíncrona: quem chama decide quando fechar.
                  event.preventDefault();
                  void onConfirm();
                }}
              >
                {confirmLabel}
              </Button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}
